// ============================================================================
// REGION FORM MODAL — Add / Edit Region (MSTR-RGN-001)
// ============================================================================
// Adds the cascading-FK pattern: Zone (parent) is a SyutSelect powered by
// options passed from the parent screen. The parent owns the lookup so it can
// also use the same data for grid-filter and refresh after-save.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { regionService } from 'api/services/regionService';
import type { Region } from 'api/types/region.types';

type RegionStatus = 'ACTIVE' | 'INACTIVE';

interface RegionFormData {
  zoneId: string;
  regionCode: string;
  regionName: string;
  statusCode: RegionStatus;
}

const INITIAL_FORM_DATA: RegionFormData = {
  zoneId: '',
  regionCode: '',
  regionName: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface ZoneOption {
  value: string;  // zoneId
  label: string;  // "ZONE_CODE — Zone Name"
}

export interface RegionFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  /** Existing region row when editing. Null/undefined for create. */
  record?: Region | null;
  /** Zone options for the parent dropdown — passed from parent screen. */
  zoneOptions: ZoneOption[];
  /** Optional seed options — used to display parents that are no longer in
   * the active list (e.g. the region's current parent zone is now INACTIVE
   * or DELETED). The seed merges with `zoneOptions` so the dropdown can render
   * the label without the user losing the selection. */
  seedZoneOptions?: ZoneOption[];
  /** Called after a successful save (parent should refetch grid). */
  onSaved: () => void;
  /** Close the modal without saving. */
  onClose: () => void;

  // ----- Minimize-to-bar support (parent passes when feature should be enabled) -----
  /** Tab id used as part of minimize modal identifier (and to bind to current tab). */
  tabId?: string;
  /** Module name shown in the MinimizedModalStrip label. */
  moduleName?: string;
  /** Called when a minimized form is restored — parent re-opens the modal in the right mode/instance/record. */
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  /** Unique instance id for create mode (lets multiple Add modals coexist). */
  createInstanceId?: string;
  /** Ref the parent assigns its 'minimize-current-modal' callback to. */
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const RegionFormModal: React.FC<RegionFormModalProps> = ({
  isOpen, mode, record, zoneOptions, seedZoneOptions, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // Merge seed options (e.g., the current inactive/deleted parent) into the active zones list
  // so the SyutSelect can render the label for values not in the active set. Dedupes by value.
  const combinedZoneOptions = useMemo(() => {
    if (!seedZoneOptions || seedZoneOptions.length === 0) return zoneOptions;
    const seedIds = new Set(seedZoneOptions.map(s => s.value));
    return [
      ...zoneOptions.filter(o => !seedIds.has(o.value)),
      ...seedZoneOptions,
    ];
  }, [zoneOptions, seedZoneOptions]);
  // ----- Form state (Pattern B) -----
  const [formData, setFormData] = useState<RegionFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  // ----- Minimize-state-capture refs (always-current values for handleMinimize closure) -----
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  // When set, the next isOpen=true effect skips the reset — used after restore from minimize.
  const isRestoreRef = useRef(false);

  // ----- Validation state -----
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // ----- Populate / reset on open -----
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    // Restore-from-minimize path: skip reset, the onRestore callback already
    // re-applied the saved formData and snapshot.
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: RegionFormData = rec
      ? {
          zoneId: rec.zoneId,
          regionCode: rec.regionCode,
          regionName: rec.regionName,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as RegionStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // ----- Dirty tracking -----
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // ----- ConfirmDialog state -----
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  // ----- Minimize handler -----
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.regionCode || currentFormData.regionCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.regionCode?.trim();
      const name = currentFormData.regionName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.regionCode || currentRecord?.regionCode || 'Region'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.regionCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Expose handleMinimize so the parent can programmatically minimize this modal
  // (e.g., when user clicks Add while another Add is already open).
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // ----- Validate, then open save confirmation -----
  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.regionCode.trim();
    const name = formData.regionName.trim();

    if (!formData.zoneId) errors.zoneId = 'Zone is required';

    if (!code) errors.regionCode = 'Region code is required';
    else if (code.length > 20) errors.regionCode = 'Max 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.regionCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.regionName = 'Region name is required';
    else if (name.length > 100) errors.regionName = 'Max 100 characters';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // ----- Confirmed save (3-stage: confirm → loading → toast) -----
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.regionCode.trim().toUpperCase();
      const name = formData.regionName.trim();

      if (mode === 'create' || mode === 'copy') {
        await regionService.create({
          zoneId: formData.zoneId,
          regionCode: code,
          regionName: name,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Region "${code}" created successfully.`);
      } else if (record) {
        await regionService.update(record.id, {
          zoneId: formData.zoneId,
          regionCode: code,
          regionName: name,
          statusCode: formData.statusCode,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Region "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This region was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A region with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save region. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {/* Loading overlay during save */}
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'view' ? 'View Region' : mode === 'copy' ? 'Copy Region' : mode === 'create' ? 'Add Region' : 'Edit Region'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          {/* Zone (parent FK). Active zones come from parent's lookup; when editing a region
              whose current parent is inactive/deleted (not in the active list), the parent
              injects a seed option so the label still resolves. Merged here instead of via
              `seedOptions` because SyutSelect only honours seedOptions in async mode. */}
          <div className="form-group" data-field="zoneId">
            <SyutSelect
              label={<>Zone <span className="text-danger">*</span></>}
              required
              options={combinedZoneOptions}
              value={formData.zoneId}
              onChange={(val) => {
                clearFieldError('zoneId');
                setFormData(prev => ({ ...prev, zoneId: (val as string) || '' }));
              }}
              error={getFieldError('zoneId')}
              isClearable={false}
              placeholder="Select a zone..."
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>

          {/* Region Code */}
          <div className="form-group" data-field="regionCode">
            <label className="form-label">
              Region Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('regionCode') ? ' form-field-error' : ''}`}
              value={formData.regionCode}
              maxLength={20}
              placeholder="NORTH-DELHI, SOUTH-TN..."
              onChange={(e) => {
                clearFieldError('regionCode');
                setFormData(prev => ({ ...prev, regionCode: e.target.value.toUpperCase() }));
              }}
              disabled={isSaving}
            />
            {getFieldError('regionCode') && (
              <div className="form-error-message">{getFieldError('regionCode')}</div>
            )}
          </div>

          {/* Region Name */}
          <div className="form-group" data-field="regionName">
            <label className="form-label">
              Region Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('regionName') ? ' form-field-error' : ''}`}
              value={formData.regionName}
              maxLength={100}
              placeholder="Delhi NCR Region"
              onChange={(e) => {
                clearFieldError('regionName');
                setFormData(prev => ({ ...prev, regionName: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('regionName') && (
              <div className="form-error-message">{getFieldError('regionName')}</div>
            )}
          </div>

          {/* Status (single source of truth — no separate isActive flag) */}
          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(val) => setFormData(prev => ({
                ...prev,
                statusCode: ((val as string) || 'ACTIVE') as RegionStatus,
              }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          (mode === 'create' || mode === 'copy')
            ? `Create region "${formData.regionCode.trim().toUpperCase()}"?`
            : `Update region "${formData.regionCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default RegionFormModal;
